import { DatePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import {
  ChartDataset,
  ChartEvent,
  ChartOptions,
  LegendElement,
  LegendItem,
  TooltipItem,
} from 'chart.js';
import { round } from 'lodash-es';
import { BaseChartDirective } from 'ng2-charts';
import { catchError, debounceTime, EMPTY, finalize } from 'rxjs';
import { CounterComponent } from '../../components/counter/counter.component';
import { ScmClickHeatmapComponent } from '../../components/scm-click-heatmap/scm-click-heatmap.component';

import {
  MatChipListbox,
  MatChipListboxChange,
  MatChipOption,
} from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import { triggerIfSlow } from '@ipg/ngx-frontend-toolset';
import {
  addDays,
  endOfDay,
  isSameDay,
  startOfDay,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalLoadersService } from '../../global-loaders.service';
import {
  ClientStatisticsParams,
  ClientStatisticsResponse,
  ClientStatisticsService,
  NumericCards,
  ScmClickBlockStatistics,
} from '../../services/client-statistics.service';

type DateRangeMap = Record<string, (value: Date) => Date>;

const dateRangeMap: DateRangeMap = {
  '7d': (d) => subDays(d, 6),
  '1m': (d) => subMonths(d, 1),
  '3m': (d) => subMonths(d, 3),
  '6m': (d) => subMonths(d, 6),
};

const CHART_COLORS = {
  completed: {
    border: 'rgb(0, 188, 125)',
    background: 'rgb(94, 233, 181)',
  },
  notCompleted: {
    border: 'rgb(254, 154, 0)',
    background: 'rgb(255, 210, 48)',
  },
  missed: {
    border: 'rgb(255, 32, 86)',
    background: 'rgb(255, 161, 173)',
  },
  planned: {
    border: 'rgb(43, 127, 255)',
    background: 'rgb(142, 197, 255)',
  },
  total: {
    border: 'rgb(97, 95, 255)',
    background: 'rgb(163, 179, 255)',
  },
  audio: {
    border: 'rgb(0, 184, 212)',
    background: 'rgb(77, 208, 225)',
  },
  image: {
    border: 'rgb(56, 142, 60)',
    background: 'rgb(129, 199, 132)',
  },
  interaction: {
    border: 'rgb(245, 127, 23)',
    background: 'rgb(255, 183, 77)',
  },
  folder: {
    border: 'rgb(94, 53, 177)',
    background: 'rgb(179, 157, 219)',
  },
  morning: {
    border: 'rgb(142, 81, 255)',
    background: 'rgb(196, 180, 255)',
  },
  afternoon: {
    border: 'rgb(246, 51, 154)',
    background: 'rgb(253, 165, 213)',
  },
  evening: {
    border: 'rgb(255, 105, 0)',
    background: 'rgb(255, 184, 106)',
  },
};

const createAlwaysVisibleLegendHandler = (
  e: ChartEvent,
  legendItem: LegendItem,
  legend: LegendElement<'line' | 'bar'>
) => {
  const index = legendItem.datasetIndex!;
  const chart = legend.chart;
  const meta = chart.getDatasetMeta(index);

  const visibleDatasets = chart.data.datasets.filter(
    (_, i) => !chart.getDatasetMeta(i).hidden
  ).length;

  if (visibleDatasets > 1) {
    meta.hidden = !meta.hidden;
  } else {
    if (meta.hidden === true) {
      meta.hidden = false;
    }
  }

  chart.update();
};

@Component({
  selector: 'app-client-statistics',
  imports: [
    MatButton,
    MatChipListbox,
    MatChipOption,
    MatDatepickerModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    ReactiveFormsModule,
    BaseChartDirective,
    CounterComponent,
    ScmClickHeatmapComponent,
    UpperCasePipe,
  ],
  providers: [DatePipe],
  templateUrl: './client-statistics.component.html',
  styleUrl: './client-statistics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientStatisticsComponent implements OnInit {
  private readonly statisticsService = inject(ClientStatisticsService);
  private readonly dialogService = inject(DialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly _datePipe = inject(DatePipe);
  private readonly _globalLoaders = inject(GlobalLoadersService);

  private static readonly LOADER_CALLER_ID = 'client_statistics_loader_caller';

  private completionSummaryData?: {
    completed: number;
    notCompleted: number;
    missed: number;
    total: number;
  };

  clientId = this.route.snapshot.parent!.paramMap.get('clientId');

  numericCards = signal<NumericCards>({
    planned: 0,
    completed: 0,
    notCompleted: 0,
    missed: 0,
  });

  scmClickBlocks = signal<ScmClickBlockStatistics[]>([]);

  /**
   * The SCM heatmap navigates day by day and deliberately ignores the global
   * period chips: its question is "what did this client do today, and
   * yesterday", which a range collapsed into one grid cannot answer.
   */
  protected readonly scmDay = signal(startOfDay(new Date()));

  protected readonly scmMaxDay = startOfDay(new Date());

  protected readonly scmDayLabel = computed(() =>
    new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(this.scmDay())
  );

  protected readonly canStepScmDayForward = computed(
    () => !isSameDay(this.scmDay(), this.scmMaxDay)
  );

  protected readonly canStepScmDayBack = computed(
    () => subDays(this.scmDay(), 1) >= this.minDate
  );

  dateRangePicker = new FormGroup({
    start: new FormControl(startOfDay(subDays(new Date(), 6))),
    end: new FormControl(endOfDay(new Date())),
  });

  minDate = subYears(new Date(), 2);
  maxDate = new Date();

  dateRangeChange$ = this.dateRangePicker.valueChanges;

  dateRangeOptions = signal([
    {
      value: '7d',
      description: 'Afgelopen 7 dagen',
    },
    {
      value: '1m',
      description: 'Afgelopen maand',
    },
    {
      value: '3m',
      description: 'Afgelopen 3 maanden',
    },
    {
      value: '6m',
      description: 'Afgelopen 6 maanden',
    },
  ]);

  dateRangeOptionSelected = signal('7d');
  isCustomDateRange = signal(false);

  plannedVsCompletedChartData: ChartDataset<'line', number[]>[] = [
    {
      data: [],
      label: 'Gepland',
      borderColor: CHART_COLORS.planned.border,
      backgroundColor: CHART_COLORS.planned.background,
    },
    {
      data: [],
      label: 'Afgerond',
      borderColor: CHART_COLORS.completed.border,
      backgroundColor: CHART_COLORS.completed.background,
    },
  ];

  chartLabels: string[] = [];
  private currentBucketSize = 'day';
  private currentBucketDateKeys: string[] = [];

  plannedVsCompletedChartOptions: ChartOptions<'line'> = {
    interaction: {
      mode: 'index',
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          precision: 0,
        },
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        onClick: createAlwaysVisibleLegendHandler,
      },
      tooltip: {
        callbacks: {
          title: (items) => this.tooltipTitle(items),
        },
      },
    },
  };

  completionStatusChartData: ChartDataset<'bar', number[]>[] = [
    {
      data: [],
      label: 'Afgerond',
      backgroundColor: CHART_COLORS.completed.background,
      borderColor: CHART_COLORS.completed.background,
    },
    {
      data: [],
      label: 'Niet afgerond',
      backgroundColor: CHART_COLORS.notCompleted.background,
      borderColor: CHART_COLORS.notCompleted.background,
    },
    {
      data: [],
      label: 'Gemist',
      backgroundColor: CHART_COLORS.missed.background,
      borderColor: CHART_COLORS.missed.background,
    },
  ];

  dailyCompletionStatusChartOptions: ChartOptions<'bar'> = {
    interaction: {
      mode: 'index',
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: true,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          precision: 0,
        },
        stacked: true,
      },
    },
    plugins: {
      legend: {
        onClick: createAlwaysVisibleLegendHandler,
      },
      tooltip: {
        callbacks: {
          title: (items) => this.tooltipTitle(items),
        },
      },
    },
  };

  completionPercentageChartData: ChartDataset<'bar', number[]>[] = [
    {
      data: [],
      label: 'Afgerond',
      backgroundColor: CHART_COLORS.completed.background,
      borderColor: CHART_COLORS.completed.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Niet afgerond',
      backgroundColor: CHART_COLORS.notCompleted.background,
      borderColor: CHART_COLORS.notCompleted.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Gemist',
      backgroundColor: CHART_COLORS.missed.background,
      borderColor: CHART_COLORS.missed.background,
      borderRadius: 4,
    },
  ];

  completionPercentageChartLabels = Array.from<number, number>(
    { length: 10 },
    (_, i) => i * 10
  );

  completionPercentageChartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    scales: {
      x: {
        min: 0,
        max: 100,
        stacked: true,
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          stepSize: 10,
          callback: (value) => `${value}%`,
        },
      },
      y: {
        stacked: true,
        ticks: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        onClick: createAlwaysVisibleLegendHandler,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (!this.completionSummaryData) {
              return context.dataset.label || '';
            }

            const datasetIndex = context.datasetIndex;
            const total = this.completionSummaryData.total;
            const percentage = round(context.parsed.x, 2);

            let count: number;
            let label: string;

            if (datasetIndex === 0) {
              count = this.completionSummaryData.completed;
              label = 'Afgerond';
            } else if (datasetIndex === 1) {
              count = this.completionSummaryData.notCompleted;
              label = 'Niet afgerond';
            } else {
              count = this.completionSummaryData.missed;
              label = 'Gemist';
            }

            return `${label}: ${percentage}% (${count} van ${total})`;
          },
        },
      },
    },
  };

  completedByTimeblockChartData: ChartDataset<'line' | 'bar', number[]>[] = [
    {
      data: [],
      label: 'Totaal',
      type: 'line',
      borderColor: CHART_COLORS.total.border,
      backgroundColor: CHART_COLORS.total.background,
    },
    {
      data: [],
      label: 'Ochtend',
      type: 'bar',
      backgroundColor: CHART_COLORS.morning.background,
      borderColor: CHART_COLORS.morning.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Middag',
      type: 'bar',
      backgroundColor: CHART_COLORS.afternoon.background,
      borderColor: CHART_COLORS.afternoon.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Avond',
      type: 'bar',
      backgroundColor: CHART_COLORS.evening.background,
      borderColor: CHART_COLORS.evening.background,
      borderRadius: 4,
    },
  ];

  robotOnlineByTimeblockChartData: ChartDataset<'line' | 'bar', number[]>[] = [
    {
      data: [],
      label: 'Totaal',
      type: 'line',
      borderColor: CHART_COLORS.total.border,
      backgroundColor: CHART_COLORS.total.background,
    },
    {
      data: [],
      label: 'Ochtend',
      type: 'bar',
      backgroundColor: CHART_COLORS.morning.background,
      borderColor: CHART_COLORS.morning.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Middag',
      type: 'bar',
      backgroundColor: CHART_COLORS.afternoon.background,
      borderColor: CHART_COLORS.afternoon.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Avond',
      type: 'bar',
      backgroundColor: CHART_COLORS.evening.background,
      borderColor: CHART_COLORS.evening.background,
      borderRadius: 4,
    },
  ];

  scmBlockChartData: ChartDataset<'line' | 'bar', number[]>[] = [
    {
      data: [],
      label: 'Totaal',
      type: 'line',
      borderColor: CHART_COLORS.total.border,
      backgroundColor: CHART_COLORS.total.background,
    },
    {
      data: [],
      label: 'Audio',
      type: 'bar',
      backgroundColor: CHART_COLORS.audio.background,
      borderColor: CHART_COLORS.audio.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Afbeelding',
      type: 'bar',
      backgroundColor: CHART_COLORS.image.background,
      borderColor: CHART_COLORS.image.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Interactie',
      type: 'bar',
      backgroundColor: CHART_COLORS.interaction.background,
      borderColor: CHART_COLORS.interaction.background,
      borderRadius: 4,
    },
    {
      data: [],
      label: 'Tags',
      type: 'bar',
      backgroundColor: CHART_COLORS.folder.background,
      borderColor: CHART_COLORS.folder.background,
      borderRadius: 4,
    },
  ];

  timeblockChartOptions: ChartOptions<'line' | 'bar'> = {
    interaction: {
      mode: 'index',
    },
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex!;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);

          const visibleDatasets = chart.data.datasets.filter(
            (_, i) => !chart.getDatasetMeta(i).hidden
          ).length;

          if (visibleDatasets > 1) {
            meta.hidden = !meta.hidden;
          } else {
            if (meta.hidden === true) {
              meta.hidden = false;
            }
          }

          chart.update();
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => this.tooltipTitle(items),
        },
      },
    },
  };

  ngOnInit() {
    if (!this.clientId) {
      return;
    }

    this.loadStatistics();
    this.loadScmClickStatistics();

    this.dateRangeChange$.pipe(debounceTime(300)).subscribe(() => {
      this.loadStatistics();
    });
  }

  /**
   * Step the SCM heatmap's day. Forward is capped at today, since there is
   * nothing to show for a day that has not started.
   */
  protected stepScmDay(days: number): void {
    const target = addDays(this.scmDay(), days);

    if (target > this.scmMaxDay || target < this.minDate) {
      return;
    }

    this.scmDay.set(target);
    this.loadScmClickStatistics();
  }

  protected goToScmToday(): void {
    this.selectScmDay(this.scmMaxDay);
  }

  /** Jump straight to a day, so a month back is one pick instead of 30 steps. */
  protected selectScmDay(day: Date | null): void {
    if (!day) {
      return;
    }

    const target = startOfDay(day);

    if (isSameDay(target, this.scmDay())) {
      return;
    }

    this.scmDay.set(target);
    this.loadScmClickStatistics();
  }

  private toUTCDate(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );
  }

  private formatBucketLabel(dateKey: string, bucketSize: string): string {
    if (bucketSize === 'day') {
      const date = new Date(dateKey);
      return date.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    if (bucketSize === 'week') {
      // dateKey = "2026-W20" → "12 mei" (Monday of that ISO week)
      const parts = dateKey.split('-W');
      const year = parseInt(parts[0], 10);
      const week = parseInt(parts[1], 10);
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
      return monday.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    // month: dateKey = "2026-05" → "mei 2026"
    const [y, m] = dateKey.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
  }

  private tooltipTitle(items: TooltipItem<'line' | 'bar'>[]): string | string[] {
    if (!items.length) {
      return '';
    }
    const index = items[0].dataIndex;
    const dateKey = this.currentBucketDateKeys[index];

    if (!dateKey || this.currentBucketSize !== 'week') {
      // For day/month buckets the default label is fine
      return items[0].label;
    }

    // Resolve ISO week key to Monday–Sunday range
    const parts = dateKey.split('-W');
    const year = parseInt(parts[0], 10);
    const week = parseInt(parts[1], 10);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const fmt = (d: Date) =>
      d.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });

    return `${fmt(monday)} – ${fmt(sunday)}`;
  }

  loadStatistics(): void {
    const dateRange = this.dateRangePicker.value;

    if (!dateRange.start || !dateRange.end || !this.clientId) {
      return;
    }

    this.chartLabels = [];

    const startDateUTC = this.toUTCDate(dateRange.start);
    const endDateUTC = this.toUTCDate(dateRange.end);

    const params: ClientStatisticsParams = {
      clientId: this.clientId,
      startDate: startDateUTC,
      endDate: endDateUTC,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    this.statisticsService
      .getStatistics(params)
      .pipe(
        triggerIfSlow(() =>
          this._globalLoaders.setActive(
            ClientStatisticsComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        ),
        finalize(() =>
          this._globalLoaders.setInactive(
            ClientStatisticsComponent.LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        ),
        catchError(() => {
          this.dialogService.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het ophalen van de statistieken voor deze cliënt. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        })
      )
      .subscribe({
        next: (response) => {
          this.updateChartsFromResponse(response);
        },
        error: (error) => {
          console.error('Failed to load statistics:', error);
        },
      });
  }

  /**
   * Load one day of the SCM heatmap.
   *
   * Independent of the global period: the window is the selected local day, so
   * the hour columns and the window agree. `toUTCDate` is deliberately not used
   * here — it reinterprets wall-clock time as UTC, which is harmless over a
   * month but would shift a single day by the timezone offset.
   *
   * The end bound is the *next* local midnight, which the endpoint treats as
   * exclusive; ending at 23:59:59 instead would leave hour 23 a fraction short
   * of whole, and it would hatch rather than report its zero.
   *
   * Fetched separately from the main statistics so a failure here leaves the
   * rest of the page intact; the heatmap simply falls back to its empty state
   * instead of raising a second alert dialog.
   */
  private loadScmClickStatistics(): void {
    if (!this.clientId) {
      return;
    }

    const day = this.scmDay();

    this.statisticsService
      .getScmClickStatistics({
        clientId: this.clientId,
        startDate: startOfDay(day),
        endDate: startOfDay(addDays(day, 1)),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      .pipe(
        catchError((error) => {
          console.error('Failed to load SCM click statistics:', error);
          this.scmClickBlocks.set([]);

          return EMPTY;
        })
      )
      .subscribe((response) => {
        this.scmClickBlocks.set(response.blocks);
      });
  }

  private updateChartsFromResponse(response: ClientStatisticsResponse): void {
    this.numericCards.set(response.numericCards);

    this.currentBucketSize = response.bucketSize;
    this.currentBucketDateKeys = response.bucketData.map((d) => d.date);

    this.chartLabels = response.bucketData.map((d) =>
      this.formatBucketLabel(d.date, response.bucketSize)
    );

    this.plannedVsCompletedChartData = [
      {
        data: response.bucketData.map((d) => d.planned),
        label: 'Gepland',
        borderColor: CHART_COLORS.planned.border,
        backgroundColor: CHART_COLORS.planned.background,
      },
      {
        data: response.bucketData.map((d) => d.completed),
        label: 'Afgerond',
        borderColor: CHART_COLORS.completed.border,
        backgroundColor: CHART_COLORS.completed.background,
      },
    ];

    this.completionStatusChartData = [
      {
        data: response.bucketData.map((d) => d.completed),
        label: 'Afgerond',
        backgroundColor: CHART_COLORS.completed.background,
        borderColor: CHART_COLORS.completed.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.notCompleted),
        label: 'Niet afgerond',
        backgroundColor: CHART_COLORS.notCompleted.background,
        borderColor: CHART_COLORS.notCompleted.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.missed),
        label: 'Gemist',
        backgroundColor: CHART_COLORS.missed.background,
        borderColor: CHART_COLORS.missed.background,
        borderRadius: 4,
      },
    ];

    const total =
      response.completionSummary.completed +
      response.completionSummary.notCompleted +
      response.completionSummary.missed;

    this.completionSummaryData = {
      completed: response.completionSummary.completed,
      notCompleted: response.completionSummary.notCompleted,
      missed: response.completionSummary.missed,
      total,
    };

    const completedPct =
      total > 0 ? (response.completionSummary.completed / total) * 100 : 0;
    const notCompletedPct =
      total > 0 ? (response.completionSummary.notCompleted / total) * 100 : 0;
    const missedPct = total > 0 ? (response.completionSummary.missed / total) * 100 : 0;

    this.completionPercentageChartData = [
      {
        data: [completedPct],
        label: 'Afgerond',
        backgroundColor: CHART_COLORS.completed.background,
        borderColor: CHART_COLORS.completed.background,
        barThickness: 120,
      },
      {
        data: [notCompletedPct],
        label: 'Niet afgerond',
        backgroundColor: CHART_COLORS.notCompleted.background,
        borderColor: CHART_COLORS.notCompleted.background,
        barThickness: 120,
      },
      {
        data: [missedPct],
        label: 'Gemist',
        backgroundColor: CHART_COLORS.missed.background,
        borderColor: CHART_COLORS.missed.background,
        barThickness: 120,
      },
    ];

    this.completedByTimeblockChartData = [
      {
        data: response.bucketData.map((d) => d.completedByTimeblock.total),
        label: 'Totaal',
        type: 'line',
        borderColor: CHART_COLORS.total.border,
        backgroundColor: CHART_COLORS.total.background,
      },
      {
        data: response.bucketData.map((d) => d.completedByTimeblock.morning),
        label: 'Ochtend',
        type: 'bar',
        backgroundColor: CHART_COLORS.morning.background,
        borderColor: CHART_COLORS.morning.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.completedByTimeblock.afternoon),
        label: 'Middag',
        type: 'bar',
        backgroundColor: CHART_COLORS.afternoon.background,
        borderColor: CHART_COLORS.afternoon.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.completedByTimeblock.evening),
        label: 'Avond',
        type: 'bar',
        backgroundColor: CHART_COLORS.evening.background,
        borderColor: CHART_COLORS.evening.background,
        borderRadius: 4,
      },
    ];

    this.robotOnlineByTimeblockChartData = [
      {
        data: response.bucketData.map((d) => d.robotOnlineByTimeblock.total),
        label: 'Totaal',
        type: 'line',
        borderColor: CHART_COLORS.total.border,
        backgroundColor: CHART_COLORS.total.background,
      },
      {
        data: response.bucketData.map((d) => d.robotOnlineByTimeblock.morning),
        label: 'Ochtend',
        type: 'bar',
        backgroundColor: CHART_COLORS.morning.background,
        borderColor: CHART_COLORS.morning.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.robotOnlineByTimeblock.afternoon),
        label: 'Middag',
        type: 'bar',
        backgroundColor: CHART_COLORS.afternoon.background,
        borderColor: CHART_COLORS.afternoon.background,
        borderRadius: 4,
      },
      {
        data: response.bucketData.map((d) => d.robotOnlineByTimeblock.evening),
        label: 'Avond',
        type: 'bar',
        backgroundColor: CHART_COLORS.evening.background,
        borderColor: CHART_COLORS.evening.background,
        borderRadius: 4,
      },
    ];

    const scm = response.scmBlockStatistics;

    this.scmBlockChartData = [
      {
        data: scm.bucketData.map((d) => d.total),
        label: 'Totaal',
        type: 'line',
        borderColor: CHART_COLORS.total.border,
        backgroundColor: CHART_COLORS.total.background,
      },
      {
        data: scm.bucketData.map((d) => d.audio),
        label: 'Audio',
        type: 'bar',
        backgroundColor: CHART_COLORS.audio.background,
        borderColor: CHART_COLORS.audio.background,
        borderRadius: 4,
      },
      {
        data: scm.bucketData.map((d) => d.image),
        label: 'Afbeelding',
        type: 'bar',
        backgroundColor: CHART_COLORS.image.background,
        borderColor: CHART_COLORS.image.background,
        borderRadius: 4,
      },
      {
        data: scm.bucketData.map((d) => d.interaction),
        label: 'Interactie',
        type: 'bar',
        backgroundColor: CHART_COLORS.interaction.background,
        borderColor: CHART_COLORS.interaction.background,
        borderRadius: 4,
      },
      {
        data: scm.bucketData.map((d) => d.folder),
        label: 'Tags',
        type: 'bar',
        backgroundColor: CHART_COLORS.folder.background,
        borderColor: CHART_COLORS.folder.background,
        borderRadius: 4,
      },
    ];
  }

  handleDatePickerOptionChange(event: MatChipListboxChange) {
    this.isCustomDateRange.set(false);
    this.dateRangeOptionSelected.set(event.value);

    const now = new Date();
    const end = endOfDay(now);

    const calculatedStart = dateRangeMap[event.value]
      ? dateRangeMap[event.value](now)
      : now;
    const start = startOfDay(calculatedStart);

    this.dateRangePicker.setValue({
      start,
      end,
    });
  }

  onApplyClicked(): void {
    const dateRange = this.dateRangePicker.value;

    if (dateRange.start && dateRange.end) {
      this.dateRangePicker.setValue({
        start: startOfDay(dateRange.start),
        end: endOfDay(dateRange.end),
      });

      this.dateRangeOptionSelected.set('');
      this.isCustomDateRange.set(true);
    }
  }

  formatDateRange(): string {
    const start = this.dateRangePicker.value.start;
    const end = this.dateRangePicker.value.end;

    if (!start || !end) {
      return 'Selecteer datumbereik';
    }

    const format = 'd MMM yyyy';
    const startStr = this._datePipe.transform(start, format);
    const endStr = this._datePipe.transform(end, format);

    return `${startStr} – ${endStr}`;
  }
}
